'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { tmdb } from '@/lib/tmdb';
import { useStore } from '@/lib/store';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Film, Tv, Star, Calendar, Loader2, Plus, Check, User } from 'lucide-react';
import Image from 'next/image';
import { format } from 'date-fns';

interface MediaSearchResult {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  profile_path?: string | null;
  backdrop_path: string | null;
  vote_average?: number;
  overview?: string;
  release_date?: string;
  first_air_date?: string;
  known_for_department?: string;
  media_type: 'tv' | 'movie' | 'person';
}

interface UserSearchResult {
  uid: string;
  username: string;
  displayName?: string;
  photoURL?: string;
  media_type: 'user';
}

type SearchResult = MediaSearchResult | UserSearchResult;

interface SearchDropdownProps {
  placeholder?: string;
  className?: string;
  onSelect?: (result: SearchResult) => void;
}

export function SearchDropdown({
  placeholder = "Search movies, TV shows, people, and users...",
  className = "",
  onSelect
}: SearchDropdownProps) {
  const router = useRouter();
  const { user } = useAuth();
  const { watchlist, addToWatchlist } = useStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [addingId, setAddingId] = useState<number | null>(null);

  const searchTimeout = useRef<NodeJS.Timeout>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchContent = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      // Search TMDB multi, popular people (filtered), and users in parallel
      const [tmdbData, peopleData, usersResponse] = await Promise.all([
        tmdb.searchMulti(searchQuery, 1),
        // Fetch first 50 pages of popular people (1000 people total) for better matches
        Promise.all([
          ...Array.from({ length: 50 }, (_, i) => tmdb.getPopularPeople(i + 1))
        ]).then((pages: any[]) => {
          const allPopularPeople = pages.flatMap(page => page.results);
          const searchLower = searchQuery.toLowerCase();
          return {
            results: allPopularPeople.filter((person: any) =>
              person.name.toLowerCase().includes(searchLower)
            )
          };
        }),
        fetch(`/api/users/search?q=${encodeURIComponent(searchQuery)}`).then(res => res.json()).catch(() => ({ users: [] }))
      ]);

      // Get all valid results from multi-search
      const multiResults = tmdbData.results.filter((item: any) =>
        (item.media_type === 'movie' && item.poster_path) ||
        (item.media_type === 'tv' && item.poster_path) ||
        (item.media_type === 'person' && item.profile_path)
      );

      // Get people from multi-search
      let allPeople = multiResults.filter((item: any) => item.media_type === 'person');

      // Add people from dedicated people search (avoiding duplicates)
      if (peopleData && peopleData.results) {
        const additionalPeople = peopleData.results
          .filter((person: any) => person.profile_path)
          .filter((person: any) => !allPeople.some((p: any) => p.id === person.id))
          .map((person: any) => ({ ...person, media_type: 'person' as const }));

        allPeople = [...allPeople, ...additionalPeople];
      }

      // Get movies/TV from multi-search
      const media = multiResults.filter((item: any) => item.media_type !== 'person');

      // Limit results: up to 3 people, up to 4 movies/TV
      const people = allPeople.slice(0, 3);
      const limitedMedia = media.slice(0, 4);

      const mediaResults: SearchResult[] = [...people, ...limitedMedia].map((item: any) => ({
        ...item,
        media_type: item.media_type
      }));

      // Process user results
      const userResults: SearchResult[] = (usersResponse.users || [])
        .slice(0, 2) // Limit to 2 user results to make room for people
        .map((user: any) => ({
          uid: user.uid,
          username: user.username,
          displayName: user.displayName,
          photoURL: user.photoURL,
          media_type: 'user' as const
        }));

      // Combine results: users first, then people, then media
      const combinedResults = [...userResults, ...mediaResults];

      setResults(combinedResults);
      setShowDropdown(combinedResults.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      setResults([]);
      setShowDropdown(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for search
    if (value.trim()) {
      searchTimeout.current = setTimeout(() => {
        searchContent(value);
      }, 300); // 300ms debounce
    } else {
      setResults([]);
      setShowDropdown(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    setSelectedIndex(-1);

    if (onSelect) {
      onSelect(result as any);
    }

    // Navigate based on result type
    if (result.media_type === 'user') {
      router.push(`/profile/${result.username}`);
    } else if (result.media_type === 'person') {
      router.push(`/person/${(result as MediaSearchResult).id}`);
    } else {
      router.push(`/${result.media_type}/${(result as MediaSearchResult).id}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        // Navigate to search results page
        router.push(`/search?q=${encodeURIComponent(query.trim())}`);
        setShowDropdown(false);
        setQuery('');
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultClick(results[selectedIndex]);
        } else if (query.trim()) {
          // Navigate to search results page
          router.push(`/search?q=${encodeURIComponent(query.trim())}`);
          setShowDropdown(false);
          setQuery('');
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const getDisplayTitle = (result: SearchResult) => {
    if (result.media_type === 'user') {
      return result.displayName || result.username;
    }
    const mediaResult = result as MediaSearchResult;
    return mediaResult.title || mediaResult.name || 'Unknown Title';
  };

  const getDisplayDate = (result: SearchResult) => {
    if (result.media_type === 'user' || result.media_type === 'person') return null;
    const mediaResult = result as MediaSearchResult;
    const date = mediaResult.release_date || mediaResult.first_air_date;
    return date ? format(new Date(date), 'yyyy') : null;
  };

  const isInWatchlist = (id: number) => {
    return watchlist.some(item => item.id === id);
  };

  const handleAddToWatchlist = async (e: React.MouseEvent, item: SearchResult) => {
    e.stopPropagation();

    if (!user) {
      router.push('/auth/signin');
      return;
    }

    // Only media can be added to watchlist
    if (item.media_type === 'user' || item.media_type === 'person') {
      return;
    }

    const mediaItem = item as MediaSearchResult;

    if (isInWatchlist(mediaItem.id)) {
      return;
    }

    setAddingId(mediaItem.id);

    try {
      const type = mediaItem.media_type || (mediaItem.first_air_date ? 'tv' : 'movie');

      if (type === 'tv') {
        // Import ContentChecker dynamically for TV shows
        const { ContentChecker } = await import('@/lib/contentChecker');

        // Fetch full show details and calculate update schedule
        const updateDetails = await ContentChecker.checkSingleShow(mediaItem.id, 0);

        const watchlistItem = {
          id: mediaItem.id,
          type: 'tv' as const,
          name: mediaItem.name || mediaItem.title || '',
          posterPath: mediaItem.poster_path,
          backdropPath: mediaItem.backdrop_path,
          overview: mediaItem.overview || '',
          watchedSeasons: [],
          lastChecked: new Date().toISOString(),
          hasNewContent: updateDetails.hasNewSeason || updateDetails.hasNewEpisodes,
          totalSeasons: updateDetails.totalSeasons || 0,
          addedAt: new Date().toISOString(),
          rating: mediaItem.vote_average || 0,
          genres: [],
          // Intelligent update scheduling
          status: updateDetails.status,
          nextCheckDate: updateDetails.nextCheckDate,
          updateFrequency: updateDetails.updateFrequency,
          // Content details populated immediately
          newSeasonNumber: updateDetails.hasNewSeason ? updateDetails.newSeasonNumber : undefined,
          nextEpisode: updateDetails.nextEpisode,
        };

        await addToWatchlist(watchlistItem);
      } else {
        // Movies - use simple logic
        const watchlistItem = {
          id: mediaItem.id,
          type: 'movie' as const,
          name: mediaItem.name || mediaItem.title || '',
          posterPath: mediaItem.poster_path,
          backdropPath: mediaItem.backdrop_path,
          overview: mediaItem.overview || '',
          lastChecked: new Date().toISOString(),
          hasNewContent: false,
          addedAt: new Date().toISOString(),
          rating: mediaItem.vote_average || 0,
          genres: []
        };

        await addToWatchlist(watchlistItem);
      }
    } catch (error) {
      // Failed to add to watchlist
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10 pointer-events-none" strokeWidth={2.5} />
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setShowDropdown(true);
            }
          }}
          className={`w-full bg-black/50 border-gray-700 pl-10 pr-10 text-white placeholder:text-gray-500 focus:border-red-500 backdrop-blur-sm ${className}`}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-gray-900/95 border border-gray-700 rounded-lg shadow-2xl backdrop-blur-sm max-h-96 overflow-y-auto">
          {results.map((result, index) => {
            const title = getDisplayTitle(result);
            const year = getDisplayDate(result);
            const isSelected = index === selectedIndex;

            const inWatchlist = isInWatchlist(result.id);
            const isAdding = addingId === result.id;

            // Determine if this is a user or media result
            const isUser = result.media_type === 'user';
            const isPerson = result.media_type === 'person';
            const isMedia = result.media_type === 'movie' || result.media_type === 'tv';

            const mediaResult = result as MediaSearchResult;
            const userResult = result as UserSearchResult;

            // Get image source
            let imageSrc: string | null = null;
            if (isUser) {
              imageSrc = userResult.photoURL || null;
            } else if (isPerson) {
              imageSrc = mediaResult.profile_path
                ? tmdb.getImageUrl(mediaResult.profile_path, 'w200')
                : null;
            } else {
              imageSrc = mediaResult.poster_path
                ? tmdb.getImageUrl(mediaResult.poster_path, 'w200')
                : null;
            }

            return (
              <div
                key={isUser ? `user-${userResult.uid}` : `${mediaResult.id}-${result.media_type}`}
                className={`w-full flex items-center gap-3 p-3 hover:bg-gray-800/50 transition-colors border-b border-gray-800/50 last:border-b-0 ${
                  isSelected ? 'bg-red-600/20 border-red-500/30' : ''
                }`}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <button
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  onClick={() => handleResultClick(result)}
                >
                  {/* Image/Avatar */}
                  <div className={`flex-shrink-0 bg-gray-800 rounded overflow-hidden ${
                    isUser || isPerson ? 'w-12 h-12 rounded-full' : 'w-12 h-16'
                  }`}>
                    {imageSrc ? (
                      <Image
                        src={imageSrc}
                        alt={title}
                        width={48}
                        height={isUser || isPerson ? 48 : 64}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-800">
                        {isUser ? (
                          <User className="h-6 w-6 text-gray-600" />
                        ) : isPerson ? (
                          <User className="h-6 w-6 text-gray-600" />
                        ) : result.media_type === 'tv' ? (
                          <Tv className="h-6 w-6 text-gray-600" />
                        ) : (
                          <Film className="h-6 w-6 text-gray-600" />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate text-sm">
                          {title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            {isUser ? (
                              <>
                                <User className="h-3 w-3 text-purple-500" />
                                <span className="text-xs text-gray-400">
                                  User
                                </span>
                              </>
                            ) : isPerson ? (
                              <>
                                <User className="h-3 w-3 text-orange-500" />
                                <span className="text-xs text-gray-400">
                                  {mediaResult.known_for_department || 'Person'}
                                </span>
                              </>
                            ) : result.media_type === 'tv' ? (
                              <>
                                <Tv className="h-3 w-3 text-green-500" />
                                <span className="text-xs text-gray-400 capitalize">
                                  TV Show
                                </span>
                              </>
                            ) : (
                              <>
                                <Film className="h-3 w-3 text-blue-500" />
                                <span className="text-xs text-gray-400 capitalize">
                                  Movie
                                </span>
                              </>
                            )}
                          </div>
                          {year && (
                            <>
                              <span className="text-xs text-gray-500">•</span>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-gray-500" />
                                <span className="text-xs text-gray-400">{year}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      {!isUser && mediaResult.vote_average && mediaResult.vote_average > 0 && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                          <span className="text-xs text-gray-400">
                            {mediaResult.vote_average.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </div>
                    {!isUser && mediaResult.overview && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {mediaResult.overview.slice(0, 120)}...
                      </p>
                    )}
                    {isUser && userResult.username && (
                      <p className="text-xs text-gray-500 mt-1">
                        @{userResult.username}
                      </p>
                    )}
                  </div>
                </button>

                {/* Add to Watchlist Button - Only for movies and TV shows */}
                {user && isMedia && (
                  <Button
                    size="sm"
                    variant={inWatchlist ? 'secondary' : 'default'}
                    className={`flex-shrink-0 h-8 px-3 ${
                      inWatchlist
                        ? 'bg-green-600/20 text-green-400 border-green-600/50 hover:bg-green-600/30'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                    onClick={(e) => handleAddToWatchlist(e, result)}
                    disabled={inWatchlist || isAdding}
                  >
                    {isAdding ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : inWatchlist ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            );
          })}

          {/* Show more results link */}
          <div className="p-3 border-t border-gray-800/50">
            <button
              className="w-full text-center text-sm text-red-400 hover:text-red-300 transition-colors"
              onClick={() => {
                router.push(`/search?q=${encodeURIComponent(query.trim())}`);
                setShowDropdown(false);
                setQuery('');
              }}
            >
              View all results for "{query}"
            </button>
          </div>
        </div>
      )}
    </div>
  );
}